import { useState } from 'react';
import { Select } from './Select';
import { BaseInfo } from './BaseInfo';
import { RequestSummary } from './RequestSummary';
import { Vitals } from './Vitals';
import { Treatments } from './Treatments';
import { OperationCriteria } from './OperationCriteria';
import { SecuringCriteria } from './SecuringCriteria';
import { TrafficAccident } from './TrafficAccident';
import { DrCarDrHeli } from './DrCarDrHeli';
import { AcceptRequestHistory } from './AcceptRequestHistory';
import { Note } from './Note';
import { Photo } from './Photo';
import { ActivityRecordPdf } from './ActivityRecordPdf';
import { PrognosisInfo } from './PrognosisInfo';
import './index.css';
import { IncidentDetailPatient } from 'sections/niigata/features/IncidentDetail/useIncidentDetailForNiigata';
import { ListVideos } from './Video';

type TabLabel =
  | 'baseInfo'
  | 'requestSummary'
  | 'vitals'
  | 'treatments'
  | 'operationCriteria'
  | 'securingCriteria'
  | 'trafficAccident'
  | 'drCarDrHeli'
  | 'acceptRequestHistory'
  | 'note'
  | 'photo'
  | 'video'
  | 'activityRecordPdf'
  | 'prognosisInfo';

type Tab = {
  /** タブのラベル。 */
  label: TabLabel;
  /** タブの値。 */
  value: string;
};

type Props = {
  /* 傷病者情報。 */
  patientInfo: IncidentDetailPatient;
  /** 傷病者を選択する関数。 */
  selectPatient: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  /* 傷病者名リスト。 */
  patientNameList: {
    /* 傷病者ID。 */
    patientId: IncidentDetailPatient['id'];
    /* 傷病者名。 */
    patientName: string;
  }[];
  /** 搬送先病院ID。 */
  destinationHospitalId?: string | null;
};

/** 傷病者詳細情報のコンポーネント。 */
export const PatientDetailInfo = ({
  patientInfo,
  selectPatient,
  patientNameList,
  destinationHospitalId,
}: Props) => {
  const [selectedTab, setSelectedTab] = useState<TabLabel>('baseInfo');

  const tabs: Tab[] = Object.keys(tabValueMap).map(key => ({
    label: key as TabLabel,
    value: tabValueMap[key as TabLabel],
  }));

  const handleClick = (tab: Tab) => setSelectedTab(tab.label);

  return (
    <div>
      <div className="bg-surface pt-[60px]">
        <div className="max-w-[1280px] px-[40px] m-auto flex flex-col gap-[24px]">
          <div className="ml-auto">
            <Select
              patientNameList={patientNameList}
              selectPatient={selectPatient}
              selectedPatientId={patientInfo.id}
            />
          </div>
          <Tabs tabs={tabs} selectedTab={selectedTab} onClick={handleClick} />
        </div>
      </div>
      <div className="pt-[24px] pb-[40px]">
        <div className="max-w-[1280px] px-[40px] m-auto">
          {selectedTab === 'baseInfo' && (
            <BaseInfo
              baseInfo={patientInfo.baseInfo}
              acceptRequestHistory={patientInfo.acceptRequestHistory}
            />
          )}
          {selectedTab === 'requestSummary' && (
            <RequestSummary requestSummary={patientInfo.requestSummary} />
          )}
          {selectedTab === 'vitals' && <Vitals vitals={patientInfo.vitals} />}
          {selectedTab === 'treatments' && (
            <Treatments treatments={patientInfo.treatments} />
          )}
          {selectedTab === 'operationCriteria' && (
            <OperationCriteria
              operationCriteria={patientInfo.operationCriteria}
            />
          )}
          {selectedTab === 'securingCriteria' && (
            <SecuringCriteria securingCriteria={patientInfo.securingCriteria} />
          )}
          {selectedTab === 'trafficAccident' && (
            <TrafficAccident trafficAccident={patientInfo.trafficAccident} />
          )}
          {selectedTab === 'drCarDrHeli' && (
            <DrCarDrHeli drCarDrHeli={patientInfo.drCarDrHeli} />
          )}
          {selectedTab === 'acceptRequestHistory' && (
            <AcceptRequestHistory
              acceptRequestHistory={patientInfo.acceptRequestHistory}
            />
          )}
          {selectedTab === 'note' && <Note note={patientInfo.note} />}
          {selectedTab === 'photo' && <Photo images={patientInfo.imageIds} />}
          {selectedTab === 'video' && (
            <ListVideos videoList={patientInfo.videoList} />
          )}
          {selectedTab === 'activityRecordPdf' && (
            <ActivityRecordPdf
              activityRecordPdf={patientInfo.activityRecordPdf}
            />
          )}
          {selectedTab === 'prognosisInfo' && (
            <PrognosisInfo
              destinationHospitalId={destinationHospitalId}
              patientId={patientInfo.id}
            />
          )}
        </div>
      </div>
    </div>
  );
};

type TabsProps = {
  /** タブの一覧。 */
  tabs: Tab[];
  /** 選択しているタブ。 */
  selectedTab: TabLabel;
  /** タブを選択したときに呼ばれるコールバック関数。 */
  onClick: (tab: Tab) => void;
};

const Tabs = ({ tabs, selectedTab, onClick: handleClick }: TabsProps) => {
  return (
    <div className="border-y border-outline bg-surface">
      <nav className="flex overflow-x-auto hidden-scrollbar" aria-label="Tabs">
        {tabs.map(tab => (
          <button
            key={tab.label}
            type="button"
            className={`
              shrink-0 border-b-2 px-[16px] py-[14px] noto-sans-jp-medium text-[14px]
              ${
                selectedTab === tab.label
                  ? 'border-primary text-primary'
                  : 'border-transparent text-textSecondary hover:text-textPrimary'
              }
            `}
            onClick={() => handleClick(tab)}
          >
            {tab.value}
          </button>
        ))}
      </nav>
    </div>
  );
};

/** タブのラベルと値をマッピングするオブジェクト。 */
const tabValueMap: Record<TabLabel, string> = {
  baseInfo: '基本情報',
  requestSummary: '要請概要',
  vitals: 'バイタル',
  treatments: '応急処置',
  operationCriteria: '実施基準',
  securingCriteria: '確保基準',
  trafficAccident: '交通事故',
  drCarDrHeli: 'ドクターカー/ドクターヘリ',
  acceptRequestHistory: '要請履歴',
  note: '備考',
  photo: '写真',
  video: '動画',
  activityRecordPdf: '活動記録PDF',
  prognosisInfo: '予後入力情報',
};
